import { lineageOf, type TypeLineage } from "../../../api/content/typeLineage";
import type { DataTypeRef } from "../../../api/content/workflowsApi";
import { PRESENTATION_PREFERENCES, PRESENTATIONS, type EditView, type PickView, type Presentation, type PresentationOperation, type PresentationPreferences, type ViewOf } from "./presentations";

/**
 * Which view shows a type (ADR-0032 D8, types-tooling R2): its own, else its most specific
 * ancestor's, else its base's. The ancestry is module B's, from the same pinned closure the
 * input is fitted in (`TypeLineage`); this walks no subclass chain of its own. Where several
 * ancestors with a view are equally near and none extends another, an explicit preference
 * decides, else the base's view: never the registry's order.
 */
export interface ResolvedPresentation<Operation extends PresentationOperation = PresentationOperation> {
  readonly presentation: Presentation<Operation>;
  /** `own`: built for the type; `ancestor`: a nearer class's; `base`: its base type's. */
  readonly via: "own" | "ancestor" | "base";
  /** The ancestors with a view that tied, when the base's view or a preference settled it. */
  readonly tied: readonly string[];
}

export function resolvePresentation<Operation extends PresentationOperation>(
  lineage: TypeLineage,
  operation: Operation,
  registry: readonly Presentation[] = PRESENTATIONS,
  preferences: PresentationPreferences = PRESENTATION_PREFERENCES,
): ResolvedPresentation<Operation> | null {
  const views = new Map(registry.filter((entry): entry is Presentation<Operation> => entry.operation === operation).map((entry) => [entry.target, entry]));
  const own = views.get(lineage.type);
  if (own !== undefined) return { presentation: own, via: "own", tied: [] };
  const extendsOf = new Map(lineage.classes.map((found) => [found.iri, new Set(found.extends)]));
  const candidates = lineage.classes.map((found) => found.iri).filter((iri) => views.has(iri));
  // Drop a candidate another candidate extends: it is less specific.
  const nearest = candidates.filter((iri) => !candidates.some((other) => other !== iri && (extendsOf.get(other)?.has(iri) ?? false)));
  const viewOf = (iri: string | null | undefined) => (iri === null || iri === undefined ? undefined : views.get(iri));
  const [only] = nearest;
  if (nearest.length === 1 && only !== undefined) {
    const presentation = views.get(only);
    if (presentation !== undefined) return { presentation, via: only === lineage.base ? "base" : "ancestor", tied: [] };
  }
  if (nearest.length > 1) {
    const preferred = viewOf(nearest.find((iri) => iri === preferences[lineage.type]));
    if (preferred !== undefined) return { presentation: preferred, via: "ancestor", tied: nearest };
    const base = viewOf(lineage.base);
    if (base !== undefined) return { presentation: base, via: "base", tied: nearest };
  }
  return null;
}

/** The field each single-valued accepted type is entered in: its own, or its nearest ancestor's. */
export function editViewsOf(accepts: readonly DataTypeRef[], lineages: readonly TypeLineage[]): ReadonlySet<EditView> {
  return viewsOf(accepts, lineages, "edit");
}

/** The picker each single-valued accepted type is chosen with: its own, or its nearest ancestor's. */
export function pickViewsOf(accepts: readonly DataTypeRef[], lineages: readonly TypeLineage[]): ReadonlySet<PickView> {
  return viewsOf(accepts, lineages, "pick");
}

function viewsOf<Operation extends "edit" | "pick">(accepts: readonly DataTypeRef[], lineages: readonly TypeLineage[], operation: Operation): ReadonlySet<ViewOf[Operation]> {
  return new Set(accepts.filter((reference) => !reference.many).flatMap((reference) => resolvePresentation(lineageOf(reference, lineages), operation)?.presentation.view ?? []));
}
